import React, { ElementType, FC, ForwardedRef, forwardRef } from 'react';
import {
  IconButton,
  IconButtonProps,
  ButtonBase,
  Tooltip,
  TooltipProps,
  Badge,
  BadgeProps,
  useTheme,
} from '@mui/material';
import { getFocusRingStyle } from '@/src/components/focusring/focusring';
import { getInteractiveStyle } from '@/src/materialui/button/interactive';

export interface HeaderButtonProps extends IconButtonProps<ElementType> {
  /**
   * Label for the tooltip or the text button
   */
  label?: TooltipProps['title'];
  /**
   * Change icon button with tooltip into button
   */
  extend?: boolean;
  /**
   * Properties applied to tooltip
   */
  tooltipProps?: Partial<TooltipProps>;
  /**
   * Properties applied to badge
   */
  badgeProps?: BadgeProps;
}

/**
 * IconButton for the action links
 */
export const HeaderButton: FC<HeaderButtonProps> = forwardRef(
  ({ children, sx, label, extend, tooltipProps, badgeProps, ...rest }, ref) => {
    const theme = useTheme();
    const { title, ...restTooltip } = tooltipProps ?? {};

    if (extend) {
      return (
        <ButtonBase
          sx={{
            ...theme.typography.list,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 1.25,
            py: 1.5,
            px: 1.25,
            // icon inside
            '& .MuiSvgIcon-root': {
              fontSize: '17px',
            },
            // disabled style
            '&.Mui-disabled': {
              color: 'disabled.tertiary',
            },
            // interactive style
            ...getInteractiveStyle(
              {
                fontWeight: '300',
              },
              {
                fontWeight: '700',
              },
            ),
            // focus ring
            ...getFocusRingStyle(theme),
            '&&:before': {
              borderRadius: '4px',
            },
            ...sx,
          }}
          ref={ref as ForwardedRef<HTMLButtonElement>}
          data-testid="HeaderButton"
          {...rest}
        >
          <Badge {...badgeProps}>{children}</Badge>
          {label || title}
        </ButtonBase>
      );
    }

    return (
      <Tooltip title={label || title} {...restTooltip}>
        <IconButton
          sx={sx}
          size="medium"
          ref={ref as ForwardedRef<HTMLButtonElement>}
          data-testid="HeaderButton"
          {...rest}
        >
          <Badge {...badgeProps}>{children}</Badge>
        </IconButton>
      </Tooltip>
    );
  },
);
